import React, { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, zIndex as Z } from '@nula/core-ui';
import styles from './floating-panel.module.css';

const floatingPanel = cva(styles.base, {
  variants: {
    compact: {
      true: styles.compact,
      false: undefined,
    },
    scrollable: {
      true: styles.scrollable,
      false: undefined,
    },
  },
  defaultVariants: { compact: false, scrollable: false },
});

interface FloatingPanelProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof floatingPanel> {
  /** Fixed position top */
  top?: number;
  /** Fixed position left */
  left?: number;
  /** Fixed position right */
  right?: number;
  /** Fixed position bottom */
  bottom?: number;
  /** Z-index layer */
  zIndex?: number;
  /** Min width */
  minWidth?: number;
  /** Max height (enables scroll) */
  maxHeight?: number;
  children: React.ReactNode;
}

/**
 * FloatingPanel — positioned portal container for toolbars, menus, and popups.
 *
 * Renders as a fixed-position panel portaled to document.body with consistent
 * styling from the design system. Uses Flex internally.
 *
 * @example
 * <FloatingPanel top={100} left={200}>
 *   <Text>Content</Text>
 * </FloatingPanel>
 */
export const FloatingPanel = forwardRef<HTMLDivElement, FloatingPanelProps>(function FloatingPanel(
  { top, left, right, bottom, zIndex = Z.modal, minWidth, maxHeight, compact = false, children, className, style, ...props },
  ref,
) {
  const hasMaxHeight = !!maxHeight;

  return createPortal(
    <Flex
      ref={ref}
      vertical
      className={clsx(floatingPanel({ compact, scrollable: hasMaxHeight }), className)}
      style={{
        '--fp-top': top !== undefined ? `${top}px` : undefined,
        '--fp-left': left !== undefined ? `${left}px` : undefined,
        '--fp-right': right !== undefined ? `${right}px` : undefined,
        '--fp-bottom': bottom !== undefined ? `${bottom}px` : undefined,
        '--fp-z-index': zIndex,
        '--fp-min-width': minWidth !== undefined ? `${minWidth}px` : undefined,
        '--fp-max-height': maxHeight !== undefined ? `${maxHeight}px` : undefined,
        ...style,
      } as React.CSSProperties}
      {...props}
    >
      {children}
    </Flex>,
    document.body,
  );
});
